import { supabase, isPlaceholderSupabase } from './supabase';
import { appendLocalVideoTag } from './demoData';
import type { CornerKey } from './rateCapture';

export type VideoPlatform = 'YOUTUBE' | 'VEO' | 'HUDL' | 'INTERNAL';
export type VideoVisibility = 'COACH_ONLY' | 'PLAYER' | 'PARENT';

const CORNER_IDS: Record<CornerKey, string> = {
  tech:   'corner-technical',
  phys:   'corner-physical',
  psych:  'corner-psych',
  social: 'corner-social',
};

export function detectPlatform(url: string): VideoPlatform {
  const u = url.toLowerCase();
  if (u.includes('youtube.com') || u.includes('youtu.be')) return 'YOUTUBE';
  if (u.includes('veo.co')) return 'VEO';
  if (u.includes('hudl.com')) return 'HUDL';
  return 'INTERNAL';
}

interface SaveOptions {
  coachId: string;
  playerId: string;
  clipUrl: string;
  tsIn: number;
  tsOut: number;
  corner: CornerKey | null;
  notes: string;
  visibility?: VideoVisibility;
}

export async function hasActiveMediaConsent(playerId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('consent_logs')
      .select('id')
      .eq('user_id', playerId)
      .eq('consent_type', 'MEDIA')
      .not('granted_at', 'is', null)
      .is('revoked_at', null)
      .limit(1);
    if (error) {
      console.warn('[video tag] media consent check failed', error.message);
      return false;
    }
    return (data ?? []).length > 0;
  } catch (err) {
    console.warn('[video tag] media consent check threw', err);
    return false;
  }
}

export async function saveVideoTag(opts: SaveOptions): Promise<{ ok: true; id: string } | { ok: false; error: string }> {
  const { coachId, playerId, clipUrl, tsIn, tsOut, corner, notes } = opts;
  const visibility = opts.visibility ?? 'PLAYER';
  if (!clipUrl.trim()) return { ok: false, error: 'Clip URL is required.' };
  if (tsOut <= tsIn) return { ok: false, error: 'End time must be after start time.' };
  // Parent-visibility check still runs offline — placeholder Supabase will
  // return false for consent, which is the safe default for minors.
  if (visibility === 'PARENT' && !(await hasActiveMediaConsent(playerId))) {
    return { ok: false, error: 'Media consent is required before a clip can be parent-visible.' };
  }

  const tsInClean = Math.max(0, Math.floor(tsIn));
  const tsOutClean = Math.max(1, Math.floor(tsOut));
  const cornerId = corner ? CORNER_IDS[corner] : null;
  const cleanNotes = notes.trim() || null;

  // Offline path — local clips read from the same key, so PlayerDetail's
  // clip list will see the tag immediately on reload.
  if (isPlaceholderSupabase()) {
    try {
      const row = appendLocalVideoTag({
        player_id: playerId,
        clip_url: clipUrl.trim(),
        platform: detectPlatform(clipUrl),
        notes: cleanNotes,
        ts_in: tsInClean,
        ts_out: tsOutClean,
        corner_id: cornerId,
      });
      // The created_by + visibility fields aren't carried in LocalVideoTag
      // (the existing demo seed doesn't use them); the consent gate above
      // already handled the parent-visibility safety case.
      void coachId;
      return { ok: true, id: row.id };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'Could not save tag locally.' };
    }
  }

  const { data, error } = await supabase
    .from('video_tags')
    .insert({
      player_id: playerId,
      created_by: coachId,
      clip_url: clipUrl.trim(),
      platform: detectPlatform(clipUrl),
      ts_in: tsInClean,
      ts_out: tsOutClean,
      corner_id: cornerId,
      notes: cleanNotes,
      visibility,
    })
    .select('id')
    .single();

  if (error || !data) return { ok: false, error: error?.message ?? 'Could not save tag.' };
  return { ok: true, id: String(data.id) };
}
